/**
 * Analytics Service - Premium Conversion Tracking
 *
 * Firebase Analytics wrapper for tracking premium subscription funnel.
 * Tracks user journey from discovery to purchase completion.
 */

import { logEvent } from 'firebase/analytics';
import { analytics } from '../config/firebase';

/**
 * Premium conversion funnel events
 */

// Track when user views premium page
export function trackPremiumPageView(userType?: 'anonymous' | 'registered' | 'premium') {
  if (!analytics) {
    console.error('[Analytics] Analytics not initialized!');
    return;
  }

  // @ts-ignore - Firebase Analytics custom parameters
  logEvent(analytics, 'view_item', {
    items: [{
      item_id: 'premium_monthly',
      item_name: 'Premium Abonelik',
      item_category: 'subscription',
      price: 199,
      currency: 'TRY',
    }],
    user_type: userType,
  });
}

// Track when user clicks "Premium'a Geç" button
export function trackBeginCheckout(params: {
  ctaLocation: 'landing_page' | 'premium_page' | 'header_dropdown' | 'premium_modal' | 'sidebar_banner';
  price?: number;
}) {
  if (!analytics) {
    console.error('[Analytics] Analytics not initialized!');
    return;
  }

  const price = params.price ?? 199;

  // @ts-ignore - Firebase Analytics custom parameters
  logEvent(analytics, 'begin_checkout', {
    currency: 'TRY',
    value: price,
    items: [{
      item_id: 'premium_monthly',
      item_name: 'Premium Abonelik',
      price: price,
      quantity: 1,
    }],
    cta_location: params.ctaLocation,
  });
}

// Track successful premium purchase (call this from PremiumSuccessPage)
export function trackPurchase(params: {
  transactionId: string;
  price?: number;
}) {
  if (!analytics) {
    console.error('[Analytics] Analytics not initialized!');
    return;
  }

  const price = params.price ?? 199;

  // @ts-ignore - Firebase Analytics custom parameters
  logEvent(analytics, 'purchase', {
    currency: 'TRY',
    transaction_id: params.transactionId,
    value: price,
    items: [{
      item_id: 'premium_monthly',
      item_name: 'Premium Abonelik',
      price: price,
      quantity: 1,
    }],
    payment_method: 'dodopayments',
  });
}

// Track premium limit modal impression
export function trackPremiumModalView(params: {
  featureName: string;
  triggeredFrom?: string;
}) {
  if (!analytics) {
    console.error('[Analytics] Analytics not initialized!');
    return;
  }

  // @ts-ignore - Firebase Analytics custom parameters
  logEvent(analytics, 'view_promotion', {
    promotion_id: 'limit_modal',
    promotion_name: 'Premium Limit Modal',
    creative_name: params.featureName,
    location_id: params.triggeredFrom || 'chat',
  });
}

// Track premium modal CTA click
export function trackPremiumModalClick(params: {
  featureName: string;
}) {
  if (!analytics) {
    console.error('[Analytics] Analytics not initialized!');
    return;
  }

  // @ts-ignore - Firebase Analytics custom parameters
  logEvent(analytics, 'select_promotion', {
    promotion_id: 'limit_modal',
    promotion_name: 'Premium Limit Modal',
    creative_name: params.featureName,
  });
}

// Track user sign up (call from AuthContext after successful registration)
export function trackSignUp(method: 'google' | 'email') {
  if (!analytics) {
    console.error('[Analytics] Analytics not initialized!');
    return;
  }

  // @ts-ignore - Firebase Analytics custom parameters
  logEvent(analytics, 'sign_up', {
    method,
  });
}

// Track user login (call from AuthContext after successful login)
export function trackLogin(method: 'google' | 'email') {
  if (!analytics) {
    console.error('[Analytics] Analytics not initialized!');
    return;
  }

  // @ts-ignore - Firebase Analytics custom parameters
  logEvent(analytics, 'login', {
    method,
  });
}

// Set user properties (persistent across sessions)
export function setUserProperties(properties: {
  userType?: 'anonymous' | 'registered' | 'premium';
  userId?: string;
}) {
  if (!analytics) {
    console.error('[Analytics] Analytics not initialized!');
    return;
  }

  // Firebase Analytics automatically tracks user_id if set
  // We'll use custom user properties for userType
  if (properties.userType) {
    // @ts-ignore - Firebase Analytics custom parameters
    logEvent(analytics, 'set_user_properties', {
      user_type: properties.userType,
    });
  }
}

// Track exceptions/errors (optional)
export function trackException(params: {
  description: string;
  fatal: boolean;
}) {
  if (!analytics) {
    console.error('[Analytics] Analytics not initialized!');
    return;
  }

  // @ts-ignore - Firebase Analytics custom parameters
  logEvent(analytics, 'exception', {
    description: params.description,
    fatal: params.fatal,
  });
}
